import React, { createContext } from "react";
import useQueryActiveRoutes from "../../../hooks/useQueryActiveRoutes";
import { connect } from "react-redux";
import { CurrentUser, ReduxState } from "../../../store/storeTypes";
import { UseQueryResult } from "@tanstack/react-query";
import {
  ActiveRouteResult,
  ResponseCompanyActiveRoutes,
  StaffMemberLocation,
} from "@mysnowscape/snow-lib";

/**
 * A layer to load the data needed for Active Routes. By putting this here, we avoid the need for each child route to load it's own data.
 * This reduces the number of queries that get sent to the backend. Even though TanStack queries will reuse the already loaded data, they
 * will still shoot off a refresh request in the background, so this approach still reduces the number of requests hitting the backend.
 */

// Create the context
export const ActiveRouteDataContext = createContext<{
  activeRoutesQuery: UseQueryResult<ResponseCompanyActiveRoutes, Error> | null;
  activeRoutes: ActiveRouteResult[] | null;
  offJobStaff: StaffMemberLocationWithColor[];
}>({
  activeRoutesQuery: null,
  activeRoutes: null,
  offJobStaff: [],
});

const ActiveRouteDataLayer = ({
  currentUser,
  children,
}: {
  currentUser: CurrentUser;
  children: React.ReactNode;
}) => {
  const activeRoutesQuery = useQueryActiveRoutes(
    currentUser.currentCompanyId!,
    true,
  );

  const activeRouteData = activeRoutesQuery.data;
  if (activeRoutesQuery.isLoading && !activeRouteData) return null;
  const activeRoutes = activeRouteData?.activeRoutes ?? null;

  let offJobStaff = extractOffJobStaff(activeRouteData);

  // Provide context to children
  return (
    <ActiveRouteDataContext.Provider
      value={{ activeRoutesQuery, offJobStaff, activeRoutes }}
    >
      {children}
    </ActiveRouteDataContext.Provider>
  );
};
const mapStateToProps = (state: ReduxState) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(ActiveRouteDataLayer);
export interface StaffMemberLocationWithColor extends StaffMemberLocation {
  color: string;
}

/**
 * Get staff that are clocked in but not on a route.
 */
function extractOffJobStaff(
  activeRouteData: ResponseCompanyActiveRoutes | undefined,
) {
  let staff = activeRouteData?.staff ?? [];
  const activeRoutes = activeRouteData?.activeRoutes;
  let offJobStaff: StaffMemberLocationWithColor[] = [];
  for (let s of staff) {
    // Look for an active route that this staff member is servicing and use that route's color for the staff member:
    const matchingRoute = activeRoutes?.find((a) => a.operator?._id === s._id);
    // @ts-ignore: we are adding a dynamic property here
    s.color = matchingRoute?.route?.color ?? "emerald";
    if (!matchingRoute && s.clockedIn) {
      offJobStaff.push(s as StaffMemberLocationWithColor);
    }
  }
  return offJobStaff;
}
